/**
 * Validates if a string is a valid GUID (Globally Unique Identifier)
 * Supports both with and without hyphens, and different GUID formats
 * @param value - The string to validate
 * @returns true if the string is a valid GUID, false otherwise
 */
export function isValidGuid(value: string | null | undefined): boolean {
  if (!value) return false;

  // Trim whitespace and convert to lowercase for comparison
  const trimmedStr = value.trim().toLowerCase();

  const guidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
  const isValid = guidRegex.test(trimmedStr);

  return isValid;
}

/**
 * Calculates the total number of days since the last login.
 * If dateAsString is not provided, uses default dates based on mitID and kombitID.
 * @param dateAsString - The last login date as a string.
 * @param slotProps - The slotProps object containing user data.
 * @returns Number of days since last login.
 */
export function totalDaysSinceLastLogin(dateAsString: string) {
  let lastLoginDate: Date;

  if (dateAsString) {
    lastLoginDate = new Date(dateAsString);
  } else {
    return "-";
  }

  const now = new Date();
  const diffTime = Math.abs(now.getTime() - lastLoginDate.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return `${diffDays} dage`;
}

/**
 * Formats the user creation date for display.
 * If dateAsString is not provided, uses default dates based on mitID and kombitID.
 * @param dateAsString - The creation date as a string.
 * @param slotProps - The slotProps object containing user data.
 * @returns Formatted date string in 'dd/MM/yyyy' format.
 */
export function displayUserCreationDate(
  dateAsString: string,
  slotProps: any
): string {
  let lastLoginDate: Date;

  if (dateAsString) {
    lastLoginDate = new Date(dateAsString);
  } else {
    // Default dates based on mitID and kombitID
    lastLoginDate =
      !slotProps.data.mitID && !slotProps.data.kombitID
        ? new Date("2025-01-01")
        : new Date("2025-09-01");
  }

  return lastLoginDate.toLocaleDateString("da-DK", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function downloadFile(
  fileName: string,
  contentType: string,
  base64Data: string
) {
  // Convert base64 to blob
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], {
    type:
      contentType ||
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  // Create and download file
  const link = document.createElement("a");

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      fileName || `logs_export_${new Date().toISOString().split("T")[0]}.xlsx`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    URL.revokeObjectURL(url);
  }
}

// Formats a valid ISO 8601 UTC timestamp as "YYYY-MM-DD HH:mm:ss".
// Preserves UTC fields without time-zone conversion and omits fractional seconds.
// With keepIsoMarkers set to true, returns "YYYY-MM-DDTHH:mm:ssZ".
// Example: "2025-11-27T07:09:11.1897155Z" becomes "2025-11-27 07:09:11",
// or "2025-11-27T07:09:11Z" when keepIsoMarkers is true.
export function displayUTCTimestamp(
  dateAsString: string,
  keepIsoMarkers?: boolean
): string {
  const year = dateAsString.substring(0, 4);
  const month = dateAsString.substring(5, 7);
  const day = dateAsString.substring(8, 10);
  const hours = dateAsString.substring(11, 13);
  const minutes = dateAsString.substring(14, 16);
  const seconds = dateAsString.substring(17, 19);

  const separator = keepIsoMarkers ? "T" : " ";
  const suffix = keepIsoMarkers ? "Z" : "";

  return `${year}-${month}-${day}${separator}${hours}:${minutes}:${seconds}${suffix}`;
}

// Converts a UTC date to Danish local time (Europe/Copenhagen, DST-aware)
export function displayDanishTimestamp(dateAsString: string): string {
  if (!dateAsString) return "";

  const date = new Date(dateAsString);
  if (isNaN(date.getTime())) return "";

  const parts = new Intl.DateTimeFormat("da-DK", {
    timeZone: "Europe/Copenhagen",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";

  return `${get("day")}.${get("month")}.${get("year")} ${get("hour")}:${get(
    "minute"
  )}:${get("second")}`;
}

export function buildExportFileName(fileNamePrefix: string): string {
  const now = new Date();
  const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(
    2,
    "0"
  )}${String(now.getDate()).padStart(2, "0")}_${String(now.getHours()).padStart(
    2,
    "0"
  )}${String(now.getMinutes()).padStart(2, "0")}${String(
    now.getSeconds()
  ).padStart(2, "0")}`;
  return `${fileNamePrefix}_${timestamp}.xlsx`;
}
